import { z } from 'zod';
import { Id } from '../primitives.js';
import { TenantKind } from './identity.js';
import { LabPartnershipStatus } from './lab-orders.js';

/**
 * Карта клиник и лабораторий.
 *
 * Карточка на карте (`DirectoryListing`) — то, что арендатор сам решил
 * показать другим: название, адрес с координатами, контакты, чем занимается.
 * Она отдельна от реквизитов клиники: реквизиты — для документов и видны
 * только своим, карточка — для поиска партнёра и видна всем участникам
 * платформы, пока стоит флаг `isPublic`.
 *
 * Координаты получает сервер по адресу через DaData; клиент может прислать их
 * сам, если человек выбрал адрес из подсказок, — тогда точка та, на которую он
 * указал, а не догадка геокодера по строке.
 *
 * Модуль: docs/03-modules/06-lab-orders.md, раздел «Карта».
 */

export const DirectoryListing = z.object({
  tenantId: Id,
  kind: TenantKind,
  name: z.string().min(1).max(200),
  address: z.string().max(500).nullable(),
  lat: z.number().min(-90).max(90).nullable(),
  lng: z.number().min(-180).max(180).nullable(),
  phone: z.string().max(32).nullable(),
  email: z.string().max(200).nullable(),
  website: z.string().max(200).nullable(),
  about: z.string().max(2000).nullable(),
  /** Чем занимается: «Циркониевые коронки», «Детская стоматология». */
  services: z.array(z.string().min(1).max(100)).max(30),
  isPublic: z.boolean(),
  rowVersion: z.number().int().nonnegative(),
  updatedAt: z.iso.datetime(),
});
export type DirectoryListing = z.infer<typeof DirectoryListing>;

/** Карточка глазами другого арендатора: без служебных полей, с состоянием партнёрства между нами. */
export const DirectoryEntry = DirectoryListing.omit({ isPublic: true, rowVersion: true }).extend({
  /** Есть ли между смотрящим и этой карточкой партнёрство и в каком оно состоянии. */
  partnership: z.object({ id: Id, status: LabPartnershipStatus }).nullable(),
  /** Собственная карточка смотрящего — на карте она есть, но предлагать себе сотрудничество нечего. */
  isSelf: z.boolean(),
});
export type DirectoryEntry = z.infer<typeof DirectoryEntry>;

export const DirectoryListingsResponse = z.object({ entries: z.array(DirectoryEntry) });
export type DirectoryListingsResponse = z.infer<typeof DirectoryListingsResponse>;

const optionalText = (max: number) => z.string().trim().max(max).nullable().default(null);

export const UpsertDirectoryListingRequest = z
  .object({
    name: z.string().trim().min(1).max(200),
    address: optionalText(500),
    lat: z.number().min(-90).max(90).nullable().default(null),
    lng: z.number().min(-180).max(180).nullable().default(null),
    phone: optionalText(32),
    email: optionalText(200),
    website: optionalText(200),
    about: optionalText(2000),
    services: z.array(z.string().trim().min(1).max(100)).max(30).default([]),
    isPublic: z.boolean().default(true),
  })
  .refine((request) => (request.lat === null) === (request.lng === null), {
    message: 'Координаты приходят парой: широта и долгота вместе или никак',
    path: ['lng'],
  });
export type UpsertDirectoryListingRequest = z.infer<typeof UpsertDirectoryListingRequest>;

/** Подсказка адреса из DaData — уже с координатами, если сервис их нашёл. */
export const AddressSuggestion = z.object({
  value: z.string(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  /** Точность координат по DaData: `0` — точный дом, дальше грубее; null — без координат. */
  precision: z.number().int().nullable(),
});
export type AddressSuggestion = z.infer<typeof AddressSuggestion>;

export const AddressSuggestResponse = z.object({ suggestions: z.array(AddressSuggestion) });
export type AddressSuggestResponse = z.infer<typeof AddressSuggestResponse>;

/**
 * Настройки карты для клиента. Ключ JavaScript API Яндекс Карт по природе
 * публичный (уходит в браузер вместе со скриптом) и защищён ограничением по
 * домену в кабинете разработчика, а не секретностью; отдавать его с сервера,
 * а не вшивать в сборку, — чтобы ключ жил в одном `.env` рядом с DaData и
 * менялся без пересборки. `null` — карта не настроена, экран покажет список.
 */
export const MapConfig = z.object({
  provider: z.literal('yandex'),
  apiKey: z.string().nullable(),
});
export type MapConfig = z.infer<typeof MapConfig>;

/**
 * Карточка для публичного сайта — без сессии и без арендатора за спиной.
 *
 * Состав уже, чем у `DirectoryEntry`: телефон и почта арендатор отдавал
 * участникам платформы, а не всему интернету, поэтому на сайт уходят только
 * название, вид, адрес с точкой, виды работ и сайт. Партнёрства и «это вы»
 * без смотрящего смысла не имеют.
 */
export const PublicDirectoryEntry = DirectoryListing.pick({
  tenantId: true,
  kind: true,
  name: true,
  address: true,
  lat: true,
  lng: true,
  services: true,
  website: true,
});
export type PublicDirectoryEntry = z.infer<typeof PublicDirectoryEntry>;

export const PublicDirectoryResponse = z.object({
  map: MapConfig,
  entries: z.array(PublicDirectoryEntry),
});
export type PublicDirectoryResponse = z.infer<typeof PublicDirectoryResponse>;
